import { datadogRum } from '@datadog/browser-rum';
import { useSession } from '@side/next-auth/react';
import { useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'react-use';
import { useSelfTeamMemberships } from '@/common/hooks/useSelfTeamMemberships';
import { useIdentity } from '$api-clients/hooks';
import { Scopes } from '$authentication/useHasPermission/constants';
import { useHasPermission } from '$authentication/useHasPermission/useHasPermission';
import {
  AGENTS_BASE_QUERY,
  buildAgentNameMatchClause,
  buildBrokerAgentsQuery,
} from './buildAgentsQuery';
import { hasNextPage, type InteractionsAgent, mergeAgents } from './helpers';

const SEARCH_DEBOUNCE_MS = 300;

type SessionUser = NonNullable<ReturnType<typeof useSession>['data']>['user'];

/**
 * `complete` — the broker query fit one page, so every option is materialized
 * and filtering is local and instant.
 *
 * `truncated` — it did not, so the full set was never loaded and narrowing has
 * to happen server-side.
 */
export type InteractionsAgentsMode = 'complete' | 'truncated';

/**
 * Members reachable by an explicit interaction-permission grant, plus the user's
 * own memberships.
 *
 * Never searched server-side: grants are used sparingly by design and these fit
 * one page, so the dropdown's local label filter narrows them. Overflow would
 * silently hide people, so it is reported rather than paginated.
 */
function useGrantedAgents(sessionUser: SessionUser | undefined) {
  const userId = sessionUser?.id;

  const sharing = useIdentity(
    '/users/{userId}/interaction-permissions/{type}/team-members',
    userId
      ? {
          params: {
            path: { userId, type: 'sharing' },
            query: { limit: 500, expand: 'team', q: AGENTS_BASE_QUERY },
          },
        }
      : null,
  );
  const submitAs = useIdentity(
    '/users/{userId}/interaction-permissions/{type}/team-members',
    userId
      ? {
          params: {
            path: { userId, type: 'submit-as' },
            query: { limit: 500, expand: 'team', q: AGENTS_BASE_QUERY },
          },
        }
      : null,
  );
  const self = useSelfTeamMemberships({ q: AGENTS_BASE_QUERY });

  const overflowed = hasNextPage(sharing.data) || hasNextPage(submitAs.data);

  useEffect(() => {
    if (!overflowed) return;

    datadogRum.addAction('Interactions agent grants exceeded one page', {
      limit: 500,
      userId,
    });
  }, [overflowed, userId]);

  const agents = useMemo(() => {
    // Self memberships first: a matching permission row overwrites them.
    const selfAgents = (self.memberships ?? []).map((membership) => ({
      id: membership.legacyExternalId ?? membership.userId,
      userId: membership.userId,
      firstName: membership.firstName,
      lastName: membership.lastName,
      teamId: membership.team.legacyExternalId ?? membership.teamId,
      teamName: membership.team.alias,
    }));

    const grantedAgents = [
      ...(sharing.data?.data ?? []),
      ...(submitAs.data?.data ?? []),
    ].map((member) => ({
      id: member.legacyExternalId ?? member.userId,
      userId: member.userId,
      firstName: member.firstName,
      lastName: member.lastName,
      teamId: member.team?.legacyExternalId ?? member.teamId,
      teamName: member.team?.alias ?? '',
    }));

    return [selfAgents, grantedAgents];
  }, [self.memberships, sharing.data, submitAs.data]);

  return {
    agents,
    loading: sharing.isLoading || submitAs.isLoading || self.loading,
    error: sharing.error ?? submitAs.error ?? self.error,
  };
}

interface BrokerAgentsParams {
  userId?: string;
  isBroker: boolean;
  isAdminBroker: boolean;
  limit: number;
  enabled: boolean;
  /** Omitted for the unsearched base load. */
  searchValue?: string;
  /** Keeps the prior page on screen while the next search request is in flight. */
  keepPreviousData: boolean;
}

/**
 * The members a broker reaches automatically by supervising a team or managing a
 * brokerage. The only source large enough to need server-side search, so it is
 * called twice: once unsearched on mount, once per debounced search term.
 */
function useBrokerAgents({
  userId,
  isBroker,
  isAdminBroker,
  limit,
  enabled,
  searchValue,
  keepPreviousData,
}: BrokerAgentsParams) {
  const broker = useIdentity(
    '/team-members',
    enabled && userId && isBroker
      ? {
          params: {
            query: {
              limit,
              q: buildBrokerAgentsQuery({
                userId,
                isAdmin: isAdminBroker,
                searchValue,
              }),
            },
          },
        }
      : null,
    { keepPreviousData },
  );

  const agents = useMemo(
    () =>
      (broker.data?.data ?? []).map((member) => ({
        id: member.legacyExternalId ?? member.userId,
        userId: member.userId,
        firstName: member.firstName,
        lastName: member.lastName,
        teamId: member.team.legacyExternalId ?? member.team.id,
        teamName: member.team.alias,
      })),
    [broker.data],
  );

  return {
    agents,
    truncated: hasNextPage(broker.data),
    loading: broker.isLoading,
    error: broker.error,
  };
}

/**
 * Fetches applied facets that fall outside the loaded page, so a selected agent
 * still gets a row to render and deselect.
 *
 * Uses the deprecated `userIds` param rather than `q` because it is the only
 * thing that accepts both id forms, splitting them server-side by UUID shape —
 * and facet ids are `legacyExternalId ?? userId`, which is exactly that mixed
 * space. `userIds` and `q` are mutually exclusive, so this carries no
 * roles/status predicate; harmless, since it only ever fetches ids the user has
 * already selected.
 */
function useHydratedAgents(missingAgentIds: string[]) {
  const hydrate = useIdentity(
    '/team-members',
    missingAgentIds.length
      ? {
          params: {
            query: { userIds: missingAgentIds.join(','), limit: 250 },
          },
        }
      : null,
  );

  return useMemo(
    () =>
      (hydrate.data?.data ?? []).map((member) => ({
        id: member.legacyExternalId ?? member.userId,
        userId: member.userId,
        firstName: member.firstName,
        lastName: member.lastName,
        teamId: member.team.legacyExternalId ?? member.team.id,
        teamName: member.team.alias,
      })),
    [hydrate.data],
  );
}

interface UseInteractionsAgentsParams {
  /** Raw input from the dropdown. Debounced, and only used in truncated mode. */
  search?: string;
  /**
   * Agent ids of the currently applied facets, in facet form
   * (`legacyExternalId ?? userId`).
   */
  selectedAgentIds?: string[];
}

const EMPTY_IDS: string[] = [];

/**
 * Agent options for the TLV people filter: the union of explicit interaction
 * permissions, the user's own memberships, and — for brokers — the members they
 * reach automatically by supervising a team or managing a brokerage.
 */
export function useInteractionsAgents({
  search = '',
  selectedAgentIds = EMPTY_IDS,
}: UseInteractionsAgentsParams = {}) {
  const sessionUser = useSession().data?.user;
  const userId = sessionUser?.id;

  const { hasPermission: isBroker } = useHasPermission({ roles: ['BROKER'] });
  // Mirrors the org-wide default view: these callers query the route bare and
  // let platform's org scoping bound the result, rather than anchoring to their
  // own broker reach.
  const { hasPermission: isAdminBroker } = useHasPermission({
    roles: ['BROKER', 'ADMIN'],
    scopes: [Scopes.INTERACTIONS_READ],
  });

  const granted = useGrantedAgents(sessionUser);
  const base = useBrokerAgents({
    userId,
    isBroker,
    isAdminBroker,
    limit: 500,
    enabled: true,
    keepPreviousData: false,
  });

  /**
   * Derived from the *unsearched* response only. A searched response's
   * `paging.next` describes the matches for that term, which says nothing about
   * whether the underlying set fits one page — so reading it here would flip the
   * hook out of server-search mode the moment a search narrowed enough.
   */
  const mode: InteractionsAgentsMode = base.truncated
    ? 'truncated'
    : 'complete';

  const [debouncedSearch, setDebouncedSearch] = useState('');
  useDebounce(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS, [search]);

  // `undefined` from the builder means the term sanitized away to nothing;
  // sending it anyway would degrade server-side into an open-text search.
  const isSearchEnabled =
    mode === 'truncated' &&
    Boolean(buildAgentNameMatchClause(debouncedSearch)) &&
    Boolean(userId);

  const searched = useBrokerAgents({
    userId,
    isBroker,
    isAdminBroker,
    limit: 50,
    enabled: isSearchEnabled,
    searchValue: debouncedSearch,
    keepPreviousData: true,
  });

  const baseAgents = useMemo(
    () => mergeAgents([...granted.agents, base.agents], userId ?? ''),
    [granted.agents, base.agents, userId],
  );

  /**
   * Measured against the base list only, never against the hydrated result —
   * otherwise a successful hydration would empty this set, drop the SWR key, and
   * flap. Ids stay "missing" here for the life of the mount, which keeps the key
   * stable.
   *
   * Restricted to truncated mode: a non-broker's sources are never cut off, so a
   * facet absent from their options is genuinely gone. It also keeps this off
   * the un-org-walled bare route for users who have no business reading it.
   */
  const missingAgentIds = useMemo(() => {
    if (mode !== 'truncated' || selectedAgentIds.length === 0) return EMPTY_IDS;

    const present = new Set(baseAgents.map((agent) => agent.id));
    return selectedAgentIds.filter((id) => !present.has(id)).slice(0, 250);
  }, [mode, selectedAgentIds, baseAgents]);

  const hydratedAgents = useHydratedAgents(missingAgentIds);

  // Hydrated rows first so a live row for the same (agent, team) wins.
  const agents: InteractionsAgent[] = useMemo(
    () =>
      mergeAgents(
        [
          hydratedAgents,
          ...granted.agents,
          base.agents,
          // `keepPreviousData` holds the last matches after the key goes null,
          // so this has to be dropped explicitly or clearing the input leaves
          // the previous search's people in the list.
          isSearchEnabled ? searched.agents : [],
        ],
        userId ?? '',
      ),
    [
      hydratedAgents,
      granted.agents,
      base.agents,
      isSearchEnabled,
      searched.agents,
      userId,
    ],
  );

  return {
    agents,
    loading: base.loading || granted.loading,
    error: base.error ?? granted.error,
    mode,
    resultsTruncated: isSearchEnabled ? searched.truncated : base.truncated,
    isSearching: isSearchEnabled && searched.loading,
    truncatedAt: isSearchEnabled ? 50 : 500,
  } as const;
}
